import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { ConfirmationService } from '@node_modules/primeng/components/common/confirmationservice';
import { ApiServiceWithLoaderService } from '@shared/services/api-service-with-loader.service';
import { FormBuilder, FormGroup } from '@angular/forms';

export class OnlineTableOptions {
  public endpoint: string;
  public noResultsMessage = 'Er zijn geen gegevens gevonden';
  public search: boolean;
  public withDelete: boolean;
  public defaultFilters?: any;
  public sort?: {
    column: string,
    order?: string
  };

  public columns: Array<any> = [
    { title: 'Name', name: 'name' }
  ];

  public constructor(options: any = {}) {
    Object.assign(this, options);
  }

  public rowDataTransformer: (data: any[]) => any[] = (data) => data;
}

@Component({
  selector: 'em-online-table',
  templateUrl: 'online-table.component.html',
  styleUrls: ['./table.scss']
})
export class OnlineTableComponent implements OnInit {
  @Input() options: OnlineTableOptions = new OnlineTableOptions();
  @Input() rows: any;
  @Output() rowClicked: EventEmitter<{ row: any, column: string }> = new EventEmitter<{ row: any, column: string }>();
  @ViewChild('dataTable') dataTable: any;

  public form: FormGroup;
  public loading: boolean;
  public totalRecords: number;

  private globalFilter$ = new Subject<string>();
  private defaultFilters = null;

  public constructor(
      private api: ApiServiceWithLoaderService,
      private confirmationService: ConfirmationService,
      private formBuilder: FormBuilder,
  ) {
    this.form = this.formBuilder.group({
      search: [''],
    });
  }

  public ngOnInit(): any {
    this.loading = true;

    this.defaultFilters = this.options && this.options.defaultFilters ? this.options.defaultFilters : null;

    // Subscribe to global filter
    this.globalFilter$.pipe(debounceTime(400),
      distinctUntilChanged(), )
      .subscribe(term => {
        this.dataTable.filterGlobal(term, 'contains');
      });
  }

  public loadLazy(event) {
    this.loading = true;

    if (this.defaultFilters) {
      if (this.defaultFilters.global) {
        event.globalFilter = this.defaultFilters.global.value;
        event.filters = { ...event.filters, global: this.defaultFilters.global };

        if (this.form && this.form.get('search')) {
          this.form.get('search').patchValue(this.defaultFilters.global.value);
        }
      }

      this.defaultFilters = null;
    }

    const pageIndex = Math.floor(event.first / event.rows) + 1;

    // Build API query
    let query = `?_pageIndex=${pageIndex}&_pageSize=${event.rows}`;

    // Global filter
    if (event.globalFilter) {
      query = `${query}&_globalFilter=${event.globalFilter}` +
        `&_globalFilterColumns=${this.options.columns.filter((column) => column.filter && column.filter.global)
          .map((column) => column.filter.field || column.name).join(',')}`;
    }

    // Column filter
    if (event.filters) {
      Object.keys(event.filters).forEach(key => {
        if (key === 'global') {
          return;
        }
        query = `${query}&${key}=${event.filters[key].value}`;
      });
    }

    // Sorting
    if (event.sortField && !this.options.sort) {
      query = `${query}&_sortColumn=${event.sortField}&_sortDirection=${event.sortOrder === 1 ? 'asc' : 'desc'}`;
    } else if (this.options.sort && this.options.sort.column) {
      query = `${query}&_sortColumn=${this.options.sort.column}&_sortDirection=${this.options.sort.order ? this.options.sort.order : 'asc'}`;
    }

    this.api.get(this.options.endpoint + query)
      .subscribe(result => {
        this.totalRecords = result.meta.pagination.total;
        this.rows = this.options.rowDataTransformer(result.data);
        this.loading = false;
    }, err => {
      this.loading = false;
    });
  }

  public onRowSelect(event) {
    this.rowClicked.emit(event.data);
  }

  public onSearchChanges(event) {
    this.globalFilter$.next(event.target.value);
  }

  public onFilterChange(column, value) {
    this.dataTable.filter(value, column, 'equals');
  }

  public onDeleteClick(value: any, event: any): void {
    event.stopPropagation();

    this.confirmationService.confirm({
      message: 'Weet u zeker dat u het geselecteerde item wilt verwijderen?',
      header: 'Bevestiging',
      icon: 'fa fa-question-circle',
      accept: () => {
        this.api.delete(`${this.options.endpoint }/${value.id}`).subscribe(() => {
          this.loadLazy({ first: 1, page: 1, filters: {}, rows: 10 });
        });
      }
    });
  }
}
